import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  TextField,
  Button,
  Typography,
  Grid,
  CircularProgress,
  Card,
  Dialog,
  Link,
  Toolbar,
  Snackbar,
  Alert,
} from '@mui/material';

import Box from './MDBox/MDBoxRoot';
import setThemeObj from './../hooks/useThemeMUI';
import { useConfiguration, Configuration } from '../hooks/useConfiguration';
import { red } from '@mui/material/colors';

type ConfigFieldDef = {
  name: string;
  label: string;
  type: 'number' | 'text' | 'boolean';
};

const CONFIG_FIELDS: ConfigFieldDef[] = [
  {
    name: 'cancel_if_no_prerequisite',
    label: 'Cancel signup if prerequisite not met (hours)',
    type: 'number',
  },
];

function ConfigField({
  config,
  def,
  onSave,
}: {
  config: Configuration;
  def: ConfigFieldDef;
  onSave: (name: string, value: any) => Promise<void>;
}) {
  const param = config.getParameterByName(def.name);
  const [value, setValue] = useState(param?.value ?? '');

  return (
    <Box display="flex" gap={2} alignItems="center">
      <TextField
        fullWidth
        type={def.type}
        label={def.label}
        value={value}
        onChange={(e) =>
          setValue(def.type === 'number' ? Number(e.target.value) : e.target.value)
        }
      />
      <Button variant="contained" onClick={() => onSave(def.name, value)}>
        Save
      </Button>
    </Box>
  );
}

export const netlifyURL = process.env.REACT_APP_NETLIFY_URL;
const REDIRECT_URI = window.location.origin + '/oauth-callback.html';

export default function SettingsPage() {
  const { config, save, loading, error } = useConfiguration();
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [testStatus, setTestStatus] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [showGmailDialog, setShowGmailDialog] = useState(false);
  const [showEditSchedPage, setShowEditSchedPage] = useState(false);

  if (loading) return <CircularProgress />;
  if (error) return <p>Error loading configuration</p>;

  return (
    <Card>
      <br />
      <Box
        component="main"
        px={2}
        py={3}
        sx={{ flexGrow: 1, width: '100%' }}
      >
        <Toolbar />
        <Grid container spacing={3}>

          {CONFIG_FIELDS.map((def) => (
            <Grid item xs={12} key={def.name}>
              <ConfigField config={config} def={def} onSave={save} />
            </Grid>
          ))}


          <Grid item xs={12}>
            <Box display="flex" alignItems="center">
              <Button
                variant="contained"
                onClick={() => {
                  setShowGmailDialog(true);
                }}
                sx={{ minWidth: 160 }}
              >
                Connect Gmail
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box display="flex" alignItems="center">
              <Button
                variant="contained"
                onClick={() => {
                  setShowEditSchedPage(true);
                }}
                sx={{ minWidth: 160 }}
              >
                Edit Scheduling Pages
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" gap={2}>
              <Button
                variant="contained"
                disabled={sendingTest}
                onClick={async () => {
                  const to = testEmail.trim();
                  if (!to) {
                    setTestStatus({ message: 'Please enter a recipient email', severity: 'error' });
                    setSnackbarOpen(true);
                    return;
                  }

                  setSendingTest(true);
                  setTestStatus(null);

                  try {
                    const resp = await fetch(`${netlifyURL || ''}/.netlify/functions/send-email`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ to, subject: 'Test Email', body: 'This is a test email.' }),
                    });

                    const payload = await resp.json().catch(() => null);

                    if (!resp.ok) {
                      console.error('send-email failed', payload);
                      const msg = payload?.error || payload || `HTTP ${resp.status}`;
                      setTestStatus({ message: `Failed to send test email: ${msg}`, severity: 'error' });
                      setSnackbarOpen(true);
                      setSendingTest(false);
                      return;
                    }

                    // send-email returns { success: true } or { success: false, error }
                    if (payload?.success === false) {
                      const err = payload?.error || 'Unknown error';
                      setTestStatus({ message: `Failed to send test email: ${err}`, severity: 'error' });
                      setSnackbarOpen(true);
                      setSendingTest(false);
                      return;
                    }

                    setTestStatus({ message: 'Test email sent (or queued) successfully', severity: 'success' });
                    setSnackbarOpen(true);
                  } catch (err: any) {
                    console.error(err);
                    setTestStatus({ message: `Error sending test email: ${err?.message ?? String(err)}`, severity: 'error' });
                    setSnackbarOpen(true);
                  } finally {
                    setSendingTest(false);
                  }
                }}
                sx={{ minWidth: 160 }}
              >
                {sendingTest ? <CircularProgress size={20} /> : 'Send Test Email'}
              </Button>

              <TextField
                label="Test recipient email"
                size="small"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
              />

              {testStatus ? (
                <Typography variant="body2" color={testStatus.severity === 'success' ? 'green' : 'error'}>
                  {testStatus.message}
                </Typography>
              ) : null}
            </Box>
          </Grid>
        </Grid>
        {<ConnectGmailDialog open={showGmailDialog} config={config} onSave={save} onClose={() => setShowGmailDialog(false)} />}
        {<EditSchedulingPageDialog open={showEditSchedPage} config={config} onSave={save} onClose={() => setShowEditSchedPage(false)} />}
      </Box>
    </Card>
  );
}

const EditSchedulingPageDialog = ({
  open,
  config,
  onSave,
  onClose,
}: {
  open: boolean;
  config: Configuration;
  onSave: (name: string, value: string) => Promise<void>;
  onClose: () => void;
}) => {

  const initialTop = config?.getStringParameterByName("CMS_schedule_top") ?? '';
  const initialSpecOffers = config?.getStringParameterByName("CMS_schedule_spec_offers") ?? '';
  const initialInfo = config?.getStringParameterByName("CMS_schedule_info") ?? '';
  const initialLoginWelcome = config?.getStringParameterByName("CMS_login_welcome") ?? '';

  const [valueTop, setValueTop] = useState(initialTop);
  const [valueSpecOffers, setValueSpecOffers] = useState(initialSpecOffers);
  const [valueInfo, setValueInfo] = useState(initialInfo);
  const [valueLoginWelcome, setValueLoginWelcome] = useState(initialLoginWelcome);
  const [saving, setSaving] = useState(false);

  const isChanged =
    valueTop !== initialTop ||
    valueSpecOffers !== initialSpecOffers ||
    valueInfo !== initialInfo ||
    valueLoginWelcome !== initialLoginWelcome;

  const modules = {
    toolbar: [
      [{ 'header': [false, 1, 2, 3, 4, 5, 6] }],
      [{ 'size': ['small', false, 'large', 'huge'] }],
      ['bold', 'italic', 'underline'],
      ['link'],
      [{ 'list': 'bullet' }],
      ['clean']
    ]
  };

  const saveData = async () => {
    setSaving(true);

    if (valueTop !== initialTop) {
      await onSave("CMS_schedule_top", valueTop);
    }
    if (valueSpecOffers !== initialSpecOffers) {
      await onSave("CMS_schedule_spec_offers", valueSpecOffers);
    }
    if (valueInfo !== initialInfo) {
      await onSave("CMS_schedule_info", valueInfo);
    }
    if (valueLoginWelcome !== initialLoginWelcome) {
      await onSave("CMS_login_welcome", valueLoginWelcome);
    }

    setSaving(false);
    onClose();
  }

  return (
    <Dialog maxWidth="md" open={open} onClose={onClose} fullWidth>
      <Box p={4} theme={setThemeObj}>

        <Typography variant="h4" gutterBottom>
          Edit Login Welcome Message
        </Typography>
        <ReactQuill
          value={valueLoginWelcome}
          onChange={setValueLoginWelcome}
          modules={modules}
          theme="snow"
        />
        <Typography variant="body2" color="textSecondary" gutterBottom>
          [[ ]]  Will be replaced with LiveChat link
        </Typography>
        <br />

        <Typography variant="h4" gutterBottom>
          Edit Scheduling Top Notice Block
        </Typography>
        <ReactQuill
          value={valueTop}
          onChange={setValueTop}
          modules={modules}
          theme="snow"
        />

        <br />
        <Typography variant="h4" gutterBottom>
          Edit Scheduling Special Offers Block
        </Typography>
        <ReactQuill
          value={valueSpecOffers}
          onChange={setValueSpecOffers}
          modules={modules}
          theme="snow"
        />

        <br />
        <Typography variant="h4" gutterBottom>
          Edit Scheduling Information Block
        </Typography>
        <ReactQuill
          value={valueInfo}
          onChange={setValueInfo}
          modules={modules}
          theme="snow"
        />

        <Box mt={2} display="flex" justifyContent="flex-end" gap={2}>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            disabled={saving || !isChanged}
            onClick={saveData}
          >
            {saving ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </Box>

      </Box>
    </Dialog>
  );
}

const ConnectGmailDialog = ({
  open,
  config,
  onSave,
  onClose,
}: {
  open: boolean;
  config: Configuration;
  onSave: (name: string, value: string) => Promise<void>;
  onClose: () => void;
}) => {
  const [clientId, setClientId] = useState(config?.getStringParameterByName("gmail_client_id") ?? '');
  const [clientSecret, setClientSecret] = useState(config?.getStringParameterByName("gmail_client_secret") ?? '');
  const [clientSecretTouched, setClientSecretTouched] = useState(false);
  const [saving, setSaving] = useState(false);


  const saveAndStartOAuth = async () => {
    setSaving(true);

    // Save Client ID
    if (clientId) {
      onSave("gmail_client_id", clientId);
    }

    // Save Client Secret
    if (clientSecret) {
      onSave("gmail_client_secret", clientSecret);
    }

    // Save Redirect URI
    onSave("gmail_redirect_URI", REDIRECT_URI);

    // Start OAuth
    const redirect = encodeURIComponent(REDIRECT_URI);
    const oauthUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${encodeURIComponent(clientId)}&redirect_uri=${redirect}&response_type=code&scope=https://www.googleapis.com/auth/gmail.send&access_type=offline&prompt=consent`;

    window.location.href = oauthUrl;
  };

  return (
    <Dialog maxWidth="sm" open={open} onClose={onClose} fullWidth>
      <Box p={4} theme={setThemeObj}>
        <Typography variant="h4" gutterBottom>
          Connect Gmail
        </Typography>

        <Typography variant="h5" gutterBottom>
          Create a Google Cloud Project
        </Typography>

        <Typography variant="body2">
          Visit:{' '}
          <Link href="https://console.cloud.google.com/" target="_blank" rel="noopener">
            https://console.cloud.google.com/
          </Link>
        </Typography>

        <Box component="ol" sx={{ pl: 3, typography: 'body2' }}>

          <li>
            <Typography variant="body2">
              In the top-left corner, click the project dropdown ➜ <strong>"New Project"</strong>.
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Enter <strong>Project name</strong> (e.g., "Scheduling Gmail Integration").
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Click <strong>"Create"</strong> and wait for the project to be created.
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Select your project from the dropdown after it’s created.
            </Typography>
          </li>
        </Box>


        <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
          Enable Gmail API
        </Typography>

        <Typography variant="body2">
          Go to:{' '}
          <Link href="https://console.cloud.google.com/apis/library" target="_blank" rel="noopener">
            https://console.cloud.google.com/apis/library
          </Link>
        </Typography>

        <Box component="ol" sx={{ pl: 3, typography: 'body2' }}>
          <li>Make sure your project is selected.</li>
          <li>Search for <strong>“Gmail API”</strong>.</li>
          <li>Click on it ➜ click <strong>"Enable"</strong>.</li>
        </Box>

        <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
          Publish the App
        </Typography>

        <Typography variant="body2">
          Go to:{' '}
          <Link href="https://console.cloud.google.com/auth/audience" target="_blank" rel="noopener">
            https://console.cloud.google.com/auth/audience
          </Link>
        </Typography>

        <Box component="ol" sx={{ pl: 3, typography: 'body2' }}>
          <li>Click Publish App</li>
          <li>Click Confirm in the confirmation dialog</li>
        </Box>

        <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
          Create OAuth 2.0 Credentials
        </Typography>
        <Typography variant="body2">
          Go to:{' '}
          <Link href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener">
            https://console.cloud.google.com/apis/credentials
          </Link>
        </Typography>
        <Box component="ol" sx={{ pl: 3, typography: 'body2' }}>
          <li>Click <strong>"Create Credentials"</strong> ➜ Select <strong>"OAuth client ID"</strong>.</li>
          <li>If prompted, click <strong>"Configure Consent Screen"</strong> first:</li>
          <Box component="ul" sx={{ pl: 3, typography: 'body2' }}>
            <li>Choose <strong>External</strong></li>
            <li>Fill in:</li>
            <Box component="ul" sx={{ pl: 4, typography: 'body2' }}>
              <li>App name: <strong>Scheduling</strong></li>
              <li>User support email</li>
              <li>Developer contact email</li>
            </Box>
            <li>Save and continue through scopes and test users for now.</li>
          </Box>
          <li>Back in the OAuth client ID setup:</li>
          <Box component="ul" sx={{ pl: 4, typography: 'body2' }}>
            <li>Application type: <strong>Web Application</strong></li>
            <li>Name: (e.g., “Scheduling”)</li>
            <li>
              Under <strong>Authorized redirect URIs</strong>, add:
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, ml: 2 }}>
                <Typography variant="body2">
                  {REDIRECT_URI}
                </Typography>
                <Button
                  size="small"
                  sx={{ ml: 1 }}
                  onClick={() => {
                    navigator.clipboard.writeText(REDIRECT_URI);
                  }}
                >
                  Copy
                </Button>
              </Box>
            </li>
            <li>Skip JavaScript origins</li>
            <li>Click <strong>Create</strong></li>
          </Box>

          <li>Copy the <strong>Client ID</strong> and <strong>Client Secret</strong></li>
        </Box>

        <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
          Paste your credentials below to start the Gmail connection process
        </Typography>

        <TextField
          fullWidth
          label="Gmail Client ID"
          value={clientId}
          onChange={(e) => setClientId(e.target.value)}
          margin="normal"
        />

        <TextField
          fullWidth
          label="Gmail Client Secret"

          value={clientSecretTouched ? clientSecret : '********'}
          onFocus={() => {
            if (!clientSecretTouched) {
              setClientSecretTouched(true);
              setClientSecret('');
            }
          }}
          onChange={(e) => {
            setClientSecretTouched(true);
            setClientSecret(e.target.value);
          }}
          margin="normal"
        />

        <Box
          sx={{
            background: '#fffbe6',
            border: '1px solid #ffe58f',
            borderRadius: 1,
            p: 2,
            my: 2,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
          }}
        >
          <Typography variant="body2" sx={{ color: '#ad8b00', fontWeight: 500 }}>
            Note:
          </Typography>
          <Typography variant="body2" sx={{ color: '#ad8b00' }}>
            When presented with <strong>Google hasn’t verified this app</strong> page, select <strong>Advanced</strong> and then <strong>Go to Scheduling (unsafe)</strong>.
          </Typography>
        </Box>

        <Box mt={2} display="flex" justifyContent="flex-end" gap={2}>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!clientId || !clientSecret || saving}
            onClick={saveAndStartOAuth}
          >
            {saving ? <CircularProgress size={24} /> : 'Connect'}
          </Button>
        </Box>

      </Box>
    </Dialog>

  );
}; 